import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ReportsService } from '../../service/reports.service';
import { SavedReport } from '../../shared/models/saved-report.model';
import { ENTITY_REGISTRY } from '../../shared/entity-registry/entity-registry';
import { NewWorkflowRequestButtonComponent } from '../../shared/components/new-workflow-request-button/new-workflow-request-button.component';
import { ReportPreviewDialogComponent } from '../../shared/components/report-preview-dialog/report-preview-dialog.component';


@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatDividerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatTooltipModule,
    NewWorkflowRequestButtonComponent
  ]
})
export class ReportsComponent implements OnInit, OnDestroy {

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private reportsService = inject(ReportsService);

  reports: SavedReport[] = [];
  displayedColumns = ['name', 'entityType', 'owner', 'updatedAt', 'actions'];

  private subscription: Subscription | null = null;

  ngOnInit(): void {
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private loadReports(): void {
    this.subscription = this.reportsService.getAllAccessibleReports().subscribe(reports => {
      this.reports = reports.sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      this.cdr.detectChanges();
    });
  }

  getEntityLabel(entityType: string): string {
    const entry = ENTITY_REGISTRY[entityType as keyof typeof ENTITY_REGISTRY];
    return entry ? entry.label : entityType;
  }

  getEntityIcon(entityType: string): string {
    const entry = ENTITY_REGISTRY[entityType as keyof typeof ENTITY_REGISTRY];
    return entry ? entry.icon : 'description';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  createNewReport(): void {
    this.router.navigate(['/securehome/report-builder']);
  }

  runReport(report: SavedReport): void {
    this.dialog.open(ReportPreviewDialogComponent, {
      data: { report },
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'report-preview-panel'
    });
  }

  editReport(report: SavedReport): void {
    this.router.navigate(['/securehome/report-builder', report.reportId]);
  }

  duplicateReport(report: SavedReport): void {
    this.reportsService.duplicateReport(report.reportId).pipe(take(1)).subscribe(newReport => {
      this.snackBar.open(`Duplicated as "${newReport.name}"`, 'Dismiss', { duration: 3000 });
      this.loadReports();
    });
  }

  deleteReport(report: SavedReport): void {
    if (!confirm(`Delete report "${report.name}"?`)) return;

    this.reportsService.deleteReport(report.reportId).pipe(take(1)).subscribe(() => {
      this.snackBar.open('Report deleted', 'Dismiss', { duration: 2000 });
      this.loadReports();
    });
  }
}
